import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import TopInfoBar from './components/TopInfoBar';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import ThematicAreas from './components/ThematicAreas';
import Events from './components/Events';
import Blog from './components/Blog';
import BlogDetail from './components/BlogDetail';
import Donation from './components/Donation';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Volunteer from './components/Volunteer';
import WhatWeDo from './components/WhatWeDo';
import Impact from './components/Impact';
import WhyChooseUs from './components/WhyChooseUs';
import CallToAction from './components/CallToAction';
import BackToTop from './components/BackToTop';
import SupportRequest from './components/SupportRequest';
import ConsentBanner from './components/ConsentBanner';
import PrivacyPolicy from './components/PrivacyPolicy';

// Layout component to wrap common elements
const Layout: React.FC<{ children: React.ReactNode; transparent?: boolean }> = ({ children, transparent = false }) => {
  return (
    <div className={`font-poppins text-text-main w-full max-w-full overflow-x-hidden min-h-screen flex flex-col ${transparent ? 'bg-transparent' : 'bg-white'}`}>
      <div className="fixed top-0 left-0 right-0 z-50 w-full">
        <TopInfoBar />
        <Header />
      </div>
      <div className="h-[95px] md:h-[90px]"></div> {/* Spacer to prevent content jump */}
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      <BackToTop />
      <ConsentBanner />
    </div>
  );
};

// Component to handle scrolling to sections and hash anchors
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

// Home component with all sections
const Home = () => (
  <>
    <Hero />
    <WhatWeDo />
    <Impact />
    <WhyChooseUs />
    <CallToAction />
  </>
);

// Events Page component that shows both recent and upcoming events
const EventsPage = () => (
  <div className="py-20 bg-white">
    <Events />
  </div>
);

// Recent Events component that shows only recent events
const RecentEvents = () => (
  <div className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <Events showOnly="recent" />
    </div>
  </div>
);

// Upcoming Events component that shows only upcoming events
const UpcomingEvents = () => (
  <div className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <Events showOnly="upcoming" />
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={
          <Layout>
            <Home />
          </Layout>
        } />
        <Route path="/home" element={
          <Layout>
            <Home />
          </Layout>
        } />
        <Route path="/about" element={
          <Layout>
            <About />
          </Layout>
        } />
        <Route path="/thematic-areas" element={
          <Layout>
            <ThematicAreas />
          </Layout>
        } />
        <Route path="/blog" element={
          <Layout>
            <Blog />
          </Layout>
        } />
        <Route path="/blog/:slug" element={
          <Layout>
            <BlogDetail />
          </Layout>
        } />
        <Route path="/contact" element={
          <Layout transparent={true}>
            <Contact />
          </Layout>
        } />
        <Route path="/donation" element={
          <Layout>
            <Donation />
          </Layout>
        } />
        <Route path="/volunteer" element={
          <Layout transparent={true}>
            <Volunteer />
          </Layout>
        } />
        <Route path="/events" element={
          <Layout>
            <EventsPage />
          </Layout>
        } />
        <Route path="/events/recent" element={
          <Layout>
            <RecentEvents />
          </Layout>
        } />
        <Route path="/events/upcoming" element={
          <Layout>
            <UpcomingEvents />
          </Layout>
        } />
        <Route path="/support-request" element={
          <Layout>
            <SupportRequest />
          </Layout>
        } />
        <Route path="/privacy-policy" element={
          <Layout>
            <PrivacyPolicy />
          </Layout>
        } />
        <Route path="/privacy" element={
          <Layout>
            <PrivacyPolicy />
          </Layout>
        } />
        {/* 404 - Keep this at the bottom */}
        <Route path="*" element={
          <Layout>
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                <p className="text-gray-600 mb-6">The page you're looking for doesn't exist or has been moved.</p>
                <Link
                  to="/"
                  className="inline-block bg-primary text-white px-6 py-2 rounded-full hover:bg-primary-dark transition-colors"
                >
                  Go to Home
                </Link>
              </div>
            </div>
          </Layout>
        } />
      </Routes>
    </Router>
  );
}

export default App;
